/**
 * PreviewClientPortal — pixel-faithful render of the ClaimSync "Client Portal"
 * template (both Daylight and Dark variants supplied), with a light/dark toggle.
 *
 * The claim header + status stepper are wired to a REAL claim (most-recent from
 * /api/v1/reports). The document checklist, clarification questions, and prep
 * guide use the template's representative content — the live portal versions are
 * magic-link / token-gated, so a design-review surface can't fetch them without
 * a client token. This is clearly a preview, not the live client portal.
 *
 * Mounted at /preview/client-portal. Self-contained — does NOT touch the real
 * /portal/:token route, the feature flags, or any working surface.
 *
 * Black + orange discipline; green ("done") only on completed stepper nodes.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/utils/api';
import { SERIF, BODY, MONO, PREVIEW_ZOOM, Palette, useTheme, ThemeToggle } from './preview/previewTheme';

interface ApiReport {
  id: string;
  client_name?: string;
  event_type?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

type StepKind = 'done' | 'active' | 'upcoming';

const STEP_DEFS = [
  { label: 'Documents', subDone: 'Collected', subActive: 'In progress', subUpcoming: 'Upcoming' },
  { label: 'Data review', subDone: 'Reconciled', subActive: 'In progress', subUpcoming: 'Upcoming' },
  { label: 'Forensic analysis', subDone: 'Complete', subActive: 'In progress', subUpcoming: 'Upcoming' },
  { label: 'Settlement', subDone: 'Complete', subActive: 'In progress', subUpcoming: 'Upcoming' },
];

// status → which step is currently active (0-based)
function activeStepFor(status?: string): number {
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'complete' || s === 'finalized') return 3;
  if (s === 'in_progress' || s === 'analyzing' || s === 'processing' || s === 'review' || s === 'in_review') return 2;
  if (s === 'draft') return 0;
  return 1;
}

const STATUS_LABEL: Record<number, string> = {
  0: 'Collecting documents',
  1: 'In data review',
  2: 'In forensic review',
  3: 'Preparing settlement',
};

// ---- representative content (template) ----
const DOCS: { label: string; meta: string; glyph: string; kind: 'done' | 'need' | 'pending'; needsUpload: boolean }[] = [
  { label: 'Profit & loss statements', meta: '2020–2023 · 4 files', glyph: '✓', kind: 'done', needsUpload: false },
  { label: 'Bank statements', meta: '11 of 12 months · March 2023 missing', glyph: '!', kind: 'need', needsUpload: true },
  { label: 'Tax returns (1065 + K-1)', meta: 'received', glyph: '✓', kind: 'done', needsUpload: false },
  { label: 'Lease & fixed obligations', meta: 'helps confirm continuing costs', glyph: '·', kind: 'pending', needsUpload: true },
];

const QUESTIONS: { text: string; answered: boolean }[] = [
  { text: 'On March 24 there was a $9,400 transfer (Zelle · J. Reyes). Was this an owner draw, a vendor payment, or something else?', answered: false },
  { text: 'Were the May payroll bonuses a one-time event, or do they recur each year?', answered: true },
];

const PREP: { title: string; desc: string }[] = [
  { title: 'Monthly, not just annual', desc: 'Month-by-month figures let us model the exact loss window.' },
  { title: 'Pre-loss baseline', desc: '2–3 years before the loss establishes your “but-for” revenue.' },
  { title: 'Anything unusual', desc: 'Owner draws, one-time costs, or big swings — flag them for us.' },
];

function StatusStepper({ P, active }: { P: Palette; active: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      {STEP_DEFS.map((s, i) => {
        const kind: StepKind = i < active ? 'done' : i === active ? 'active' : 'upcoming';
        const circleBg = kind === 'done' ? P.greenSolid : kind === 'active' ? P.orange : P.elevBg;
        const circleBd = kind === 'upcoming' ? `1.5px solid ${P.accentNeutral}` : 'none';
        const circleFg = kind === 'upcoming' ? P.textMuted : P.onOrange;
        const mark = kind === 'done' ? '✓' : kind === 'active' ? '●' : '';
        const sub = kind === 'done' ? s.subDone : kind === 'active' ? s.subActive : s.subUpcoming;
        const lineDone = i <= active; // line entering this node is "done" up to the active node
        return (
          <div key={s.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
            {i > 0 && (
              <div style={{ position: 'absolute', top: 13, left: '-50%', width: '100%', height: 2, background: lineDone ? P.greenSolid : P.accentNeutral }} />
            )}
            <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, zIndex: 1, background: circleBg, border: circleBd, color: circleFg }}>{mark}</div>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 10, color: kind === 'upcoming' ? P.textMuted : P.textPrimary }}>{s.label}</div>
            <div style={{ fontSize: 11, color: P.textMuted, marginTop: 2, textAlign: 'center' }}>{sub}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function PreviewClientPortal() {
  const { theme, P, toggle } = useTheme();
  const [report, setReport] = useState<ApiReport | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=200', { showErrorToast: false });
        const list: ApiReport[] = Array.isArray(data?.reports) ? data.reports : Array.isArray(data) ? data : [];
        // most-recently updated claim as the representative portal subject
        const sorted = [...list].sort((a, b) =>
          new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
        if (alive) setReport(sorted[0] || null);
      } catch {
        if (alive) setReport(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  const clientName = report?.client_name || 'Riverside Kitchen & Tap, LLC';
  const firstName = clientName.split(/[ ,]/)[0] || 'there';
  const claimNo = report ? `Claim ${(report.id || '').slice(0, 8).toUpperCase()}` : 'Claim HFS-TX-2023-88214';
  const active = useMemo(() => activeStepFor(report?.status), [report]);
  const initials = clientName.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'JR';

  const dmark = (kind: 'done' | 'need' | 'pending'): React.CSSProperties => {
    const base: React.CSSProperties = { width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flex: '0 0 24px' };
    if (kind === 'done') return { ...base, background: P.greenTintBg, color: P.greenText };
    if (kind === 'need') return { ...base, background: 'rgba(255,102,0,0.15)', color: P.orangeText };
    return { ...base, background: P.elevBg, color: P.textMuted };
  };

  return (
    <div style={{ zoom: PREVIEW_ZOOM, height: '100vh', width: '100%', overflow: 'hidden', background: P.pageBg, fontFamily: BODY, color: P.textBody, display: 'flex', flexDirection: 'column' }}>
      {/* Static preview banner */}
      <div style={{ background: P.orange, color: '#1a1a1a', padding: '10px 20px', fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto' }}>
        <span style={{ textTransform: 'uppercase', letterSpacing: '1px', border: '1.5px solid #1a1a1a', borderRadius: 4, padding: '2px 8px', fontSize: 10.5 }}>Static design preview</span>
        <span>These numbers are NOT live.</span>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
      {/* top brand bar */}
      <header style={{ height: 62, background: P.pageBg, borderBottom: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', padding: '0 28px', gap: 14, position: 'sticky', top: 0, zIndex: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: P.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 30px' }}>
            <div style={{ width: 12, height: 12, border: '2.5px solid #fefefe', borderRadius: '50%' }} />
          </div>
          <div style={{ fontFamily: SERIF, color: P.textPrimary, fontSize: 20 }}>The Figure Co.</div>
        </div>
        <div style={{ marginLeft: 18, paddingLeft: 18, borderLeft: `1px solid ${P.cardBorder}`, lineHeight: 1.2 }}>
          <div style={{ fontSize: 13, color: P.textPrimary, fontWeight: 500 }}>{clientName}</div>
          <div style={{ fontSize: 11, color: P.textMuted, fontFamily: MONO }}>{claimNo}</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 13, color: P.textLabel, cursor: 'pointer' }}>Need help?</span>
          <ThemeToggle P={P} theme={theme} onToggle={toggle} />
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: P.elevBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.textBody, fontWeight: 600, fontSize: 12.5 }}>{initials}</div>
        </div>
      </header>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '34px 28px 60px' }}>
        {/* greeting */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: SERIF, fontSize: 30, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Hi {firstName} — here's where your claim stands</h1>
          <p style={{ margin: '8px 0 0', color: P.textLabel, fontSize: 15 }}>Your forensic team is reconstructing the business interruption loss. You can track progress, upload documents, and answer a couple of questions below.</p>
        </div>

        {/* status tracker */}
        <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 16, padding: '26px 28px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, letterSpacing: '1px', textTransform: 'uppercase', color: P.orangeText, fontWeight: 600, marginBottom: 5 }}>Current status</div>
              <div style={{ fontFamily: SERIF, fontSize: 22, color: P.textPrimary }}>{STATUS_LABEL[active]}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: P.textMuted }}>Estimated demand-ready</div>
              <div style={{ fontSize: 15, color: P.textPrimary, fontWeight: 500, marginTop: 2 }}>Early July 2026</div>
            </div>
          </div>
          <StatusStepper P={P} active={active} />
        </div>

        {/* two column */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
          {/* documents */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 16, overflow: 'hidden' }}>
              <div style={{ padding: '18px 20px', borderBottom: `1px solid ${P.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontFamily: SERIF, fontSize: 18, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Documents</h2>
                <span style={{ fontSize: 12, color: P.greenText, fontWeight: 600 }}>9 of 11 received</span>
              </div>
              {DOCS.map((d) => (
                <div key={d.label} style={{ padding: '13px 20px', borderBottom: `1px solid ${P.rowDivider}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={dmark(d.kind)}>{d.glyph}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: P.textPrimary, fontWeight: 500 }}>{d.label}</div>
                    <div style={{ fontSize: 11.5, color: P.textMuted }}>{d.meta}</div>
                  </div>
                  {d.needsUpload && (
                    <button style={{ background: P.orange, color: P.onOrange, border: 'none', borderRadius: 7, padding: '6px 13px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: BODY }}>Upload</button>
                  )}
                </div>
              ))}
              <div style={{ padding: '16px 20px' }}>
                <div style={{ border: `1.5px dashed ${P.accentNeutral}`, borderRadius: 11, padding: 22, textAlign: 'center', cursor: 'pointer' }}>
                  <div style={{ fontSize: 20, color: P.textMuted, marginBottom: 6 }}>⤓</div>
                  <div style={{ fontSize: 13, color: P.textBody, fontWeight: 500 }}>Drag files here, or <span style={{ color: P.orangeText }}>browse</span></div>
                  <div style={{ fontSize: 11, color: P.textMuted, marginTop: 3 }}>PDF, Excel, CSV, or photos · up to 50 MB</div>
                </div>
              </div>
            </div>
          </div>

          {/* clarifications + prep */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 16, overflow: 'hidden' }}>
              <div style={{ padding: '18px 20px', borderBottom: `1px solid ${P.divider}`, display: 'flex', alignItems: 'center', gap: 9 }}>
                <h2 style={{ fontFamily: SERIF, fontSize: 18, color: P.textPrimary, margin: 0, fontWeight: 400, flex: 1 }}>A few questions</h2>
                <span style={{ fontSize: 11, fontWeight: 600, background: P.orangeTintBg, color: P.orangeText, borderRadius: 999, padding: '3px 10px' }}>1 needs you</span>
              </div>
              {QUESTIONS.map((q, i) => (
                <div key={i} style={{ padding: '15px 20px', borderBottom: `1px solid ${P.rowDivider}` }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flex: '0 0 22px', background: q.answered ? P.greenTintBg : 'rgba(255,102,0,0.15)', color: q.answered ? P.greenText : P.orangeText }}>{q.answered ? '✓' : '?'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13.5, color: P.textPrimary, fontWeight: 500, lineHeight: 1.4 }}>{q.text}</div>
                      {q.answered ? (
                        <div style={{ fontSize: 12, color: P.greenText, marginTop: 5 }}>✓ Answered</div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: P.pageBg, border: `1px solid ${P.cardBorder}`, borderRadius: 8, padding: '9px 12px', marginTop: 9 }}>
                          <span style={{ flex: 1, color: P.textMuted, fontSize: 12.5 }}>Type your answer…</span>
                          <span style={{ color: P.orangeText, fontSize: 15 }}>↵</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 16, padding: 20 }}>
              <h2 style={{ fontFamily: SERIF, fontSize: 18, color: P.textPrimary, margin: '0 0 4px', fontWeight: 400 }}>Preparing your documents</h2>
              <p style={{ fontSize: 12.5, color: P.textMuted, margin: '0 0 14px' }}>A quick guide to what helps your claim most.</p>
              {PREP.map((p) => (
                <div key={p.title} style={{ display: 'flex', gap: 11, alignItems: 'flex-start', padding: '8px 0' }}>
                  <span style={{ color: P.orangeText, fontSize: 13, marginTop: 1 }}>◆</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: P.textBody, fontWeight: 500 }}>{p.title}</div>
                    <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 1 }}>{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
